import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import OAuthButtons from "@/components/OAuthButtons";

const OAUTH_ERROR_MESSAGES = {
  oauth: "Google sign-in couldn't be completed. Please try again.",
  oauth_empty: "The sign-in link was empty. Please try again.",
  oauth_cancelled: "Google sign-in was cancelled.",
  session_expired: "Your session expired. Please sign in again.",
};

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user && !user.is_guest) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    const errKey = new URLSearchParams(location.search).get("error");
    if (errKey) {
      setError(OAUTH_ERROR_MESSAGES[errKey] || "Sign-in failed. Please try again.");
    }
  }, [location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <div className="ambient-bg" />

      {/* Left panel */}
      <div className="hidden md:flex flex-1 relative border-r border-zinc-900 p-12 flex-col justify-between">
        <BrandLogo variant="lockup" iconSize={36} to="/" testid="login-brand" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-4">
            welcome back
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-zinc-50 leading-tight">
            The interview
            <br />
            starts <span className="text-amber-400">before</span> the interview.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            Pick up your preparation where you left off: fit analysis, resume improvement, cover letter,
            question prep, and mock interviews, all in one flow.
          </p>
        </div>
        <div className="text-xs text-zinc-600 tracking-widest uppercase">
          © 2026 Intuiterview
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-in">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Sign in</h1>
            <p className="text-sm text-zinc-500 mt-1">Continue your prep journey.</p>
          </div>

          <button
            type="button"
            data-testid="login-google-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full mb-2 inline-flex items-center justify-center gap-2 bg-zinc-50 text-zinc-950 hover:bg-white font-medium rounded-lg px-4 py-2.5 transition-colors"
            style={{ display: "none" }}
          >
            hidden fallback
          </button>

          <OAuthButtons testidPrefix="login" />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full h-px bg-zinc-800" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
              <span className="bg-[#09090B] px-3 text-zinc-600">or with email</span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-widest text-zinc-500">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400"
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              data-testid="login-to-register-link"
              className="text-zinc-200 hover:text-white font-medium"
            >
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

